import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { boutiqueItemsAPI } from '../../api/boutiqueItemsAPI';
import { toast } from 'react-toastify';
import LocationPicker from '../../components/common/LocationPicker';
import { authAPI } from '../../api/authAPI';
import styled from 'styled-components';
import ListingFeeCallout from '../../components/onboarding/ListingFeeCallout';
import ListingMediaUploadField from '../../components/items/ListingMediaUploadField';
import ListingSizeColorFields from '../../components/items/ListingSizeColorFields';
import ListingCategorySelect from '../../components/items/ListingCategorySelect';
import { LUXURY_ITEM_CATEGORIES } from '../../constants/preOwnedItemCategories';
import {
  EMPTY_CATEGORY_TAXONOMY,
  buildTaxonomySubcategory,
  validateCategoryTaxonomy,
} from '../../constants/listingCategoryTaxonomy';
import {
  getListingSizeColorAttributes,
  buildSizeColorPayload,
} from '../../constants/listingSizeColorAttributes';
import {
  countShopImageFiles,
} from '../../utils/shopMedia';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
  padding: 2rem 1rem 4rem;
`;

const FormCard = styled.div`
  max-width: 560px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.8);
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
  text-align: center;
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
  }
  
  &[type="file"] {
    padding: 0.5rem;
    cursor: pointer;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  font-family: inherit;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: #fff;
  color: #1e293b;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #22c55e;
  }
`;

const PriceNote = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #166534;
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 0.8rem;
`;

const ShopBrandNote = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;

  a {
    color: #166534;
    font-weight: 600;
  }
`;

const PrimaryButton = styled.button`
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const OfferBoutiqueItemPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [shop, setShop] = useState(null);
  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    price: '',
    color: '',
    ...EMPTY_CATEGORY_TAXONOMY,
  });
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [errors, setErrors] = useState({});
  const listingAttrs = getListingSizeColorAttributes(formData.category);
  const [selectedImages, setSelectedImages] = useState([]);
  const mediaUploadApiRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(user?.shipping_address || '');
  const [addressCoords, setAddressCoords] = useState({
    latitude: user?.latitude ?? null,
    longitude: user?.longitude ?? null,
  });
  const [addressConfirmed, setAddressConfirmed] = useState(Boolean(user?.shipping_address));

  React.useEffect(() => {
    const initialAddress = user?.shipping_address || '';
    setShippingAddress(initialAddress);
    setAddressConfirmed(Boolean(initialAddress));
    setAddressCoords({
      latitude: user?.latitude ?? null,
      longitude: user?.longitude ?? null,
    });
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    boutiqueItemsAPI.getMyShop()
      .then((data) => setShop(data || null))
      .catch(() => setShop(null));
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleTaxonomyChange = (patch) => {
    const categoryChanged = 'category' in patch;
    setFormData((prev) => ({
      ...prev,
      ...patch,
      ...(categoryChanged ? { color: '' } : {}),
    }));
    if (categoryChanged) setSelectedSizes([]);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => { delete next[key]; });
      if (categoryChanged) {
        delete next.color;
        delete next.sizes;
      }
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Item name is required';
    } else if (formData.itemName.trim().length < 3) {
      newErrors.itemName = 'Item name must be at least 3 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 25) {
      newErrors.description = `Description must be at least 25 characters (${formData.description.trim().length}/25)`;
    }

    Object.assign(newErrors, validateCategoryTaxonomy(formData));

    if (listingAttrs) {
      if (!formData.color) {
        newErrors.color = 'Color is required';
      }
      if (listingAttrs.requiresSize && selectedSizes.length === 0) {
        newErrors.sizes = 'Select at least one size';
      }
    }

    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    } else {
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        newErrors.price = 'Price must be a valid positive number';
      }
    }

    if (!shippingAddress.trim()) {
      newErrors.shipping_address = 'Location is required before listing';
    }
    if (addressCoords.latitude == null || addressCoords.longitude == null) {
      newErrors.shipping_address = 'Please pick your location using the map picker';
    }
    if (!addressConfirmed) {
      newErrors.address_confirm = 'Please confirm your saved location before listing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const persistListingLocation = async () => {
    setSavingAddress(true);
    try {
      const payload = {
        shipping_address: shippingAddress.trim(),
        latitude: addressCoords.latitude,
        longitude: addressCoords.longitude,
      };
      const response = await authAPI.updateProfile(payload);
      dispatch({ type: 'auth/updateUser', payload: response });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please log in to offer your boutique item');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    if (selectedImages.length < 3) {
      toast.error(`Please upload at least 3 photos/videos. You have ${selectedImages.length}.`);
      return;
    }
    if (countShopImageFiles(selectedImages) < 1) {
      toast.error('Include at least one photo so the item can show a thumbnail. Videos are optional extras.');
      return;
    }

    try {
      await persistListingLocation();
    } catch (locationError) {
      const message = locationError?.response?.data?.error || 'Could not save location. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Could not save location. Please try again.');
      return;
    }

    setUploading(true);

    try {
      const { uploadCatalogItemResumable } = await import('../../utils/catalogResumableUpload');
      const sizeColor = buildSizeColorPayload(formData.color, selectedSizes);
      const resolvedSubcategory = buildTaxonomySubcategory(formData);

      // Media starts uploading the moment it is picked. Restart anything that
      // never began (or failed) and wait for everything to settle.
      const outcomes = mediaUploadApiRef.current
        ? await mediaUploadApiRef.current.waitForUploads()
        : selectedImages.map(() => null);
      const failedCount = outcomes.filter((o) => !o || (!o.key && !o.deferred)).length;
      if (failedCount > 0) {
        toast.error(
          `${failedCount} photo/video failed to upload. Tap Retry on the failed thumbnail, then submit again.`,
        );
        return;
      }

      await uploadCatalogItemResumable({
        kind: 'boutique',
        api: {
          createDraft: boutiqueItemsAPI.createBoutiqueItemDraft,
          uploadMedia: boutiqueItemsAPI.uploadBoutiqueItemMedia,
          finalize: boutiqueItemsAPI.finalizeBoutiqueItemDraft,
          initDirectUpload: boutiqueItemsAPI.initBoutiqueDirectUpload,
          signDirectParts: boutiqueItemsAPI.signBoutiqueDirectParts,
          completeDirectUpload: boutiqueItemsAPI.completeBoutiqueDirectUpload,
          abortDirectUpload: boutiqueItemsAPI.abortBoutiqueDirectUpload,
          attachPending: boutiqueItemsAPI.attachBoutiquePendingImages,
        },
        fields: {
          ...(shop?.brand_name ? { brand_name: shop.brand_name } : {}),
          item_name: formData.itemName.trim(),
          description: formData.description.trim(),
          price: String(parseFloat(formData.price)),
          category: formData.category,
          subcategory: resolvedSubcategory || undefined,
          color: sizeColor.color,
          size: sizeColor.size,
          available_sizes: sizeColor.available_sizes,
        },
        media: selectedImages.map((img, i) => ({
          file: img.file,
          ...(outcomes[i]?.key ? { pendingKey: outcomes[i].key } : {}),
        })),
      });
      toast.success('Your boutique item was submitted for admin review.');
      navigate('/boutiques');
    } catch (error) {
      console.error('Error creating boutique item:', error);
      const msg = error.response?.data?.error || error?.message || (typeof error === 'string' ? error : 'Failed to list item');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <PageWrapper>
        <FormCard>
          <PageTitle>Offer Item as Boutique</PageTitle>
          <p style={{ textAlign: 'center', color: '#64748b' }}>Please log in to offer your boutique item.</p>
        </FormCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <FormCard>
        <PageTitle>Offer Item as Boutique</PageTitle>
        <PageSubtitle>List your boutique items and reach buyers who love unique pieces</PageSubtitle>
        <ListingFeeCallout tier="business" />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Listing Location *</Label>
            {shippingAddress.trim() ? (
              <div style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: '#f8fafc', color: '#1e293b' }}>
                {shippingAddress}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                Pick your address from Google location picker to continue.
              </p>
            )}
            <LocationPicker
              value={shippingAddress}
              onChange={(address, coords) => {
                setShippingAddress(address);
                setAddressConfirmed(false);
                if (coords?.latitude != null && coords?.longitude != null) {
                  setAddressCoords({ latitude: coords.latitude, longitude: coords.longitude });
                }
                if (errors.shipping_address || errors.address_confirm) {
                  setErrors((prev) => ({ ...prev, shipping_address: '', address_confirm: '' }));
                }
              }}
            />
            {shippingAddress.trim() ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={addressConfirmed}
                  onChange={(e) => setAddressConfirmed(e.target.checked)}
                />
                Confirm this is my listing location
              </label>
            ) : null}
            {errors.shipping_address && <ErrorText>{errors.shipping_address}</ErrorText>}
            {errors.address_confirm && <ErrorText>{errors.address_confirm}</ErrorText>}
          </FormGroup>

          <ShopBrandNote>
            This item will be listed under <strong>{shop?.brand_name || 'your boutique'}</strong>.
            {' '}To change the brand name or logo,{' '}
            <Link to="/boutique-shop/edit">edit your boutique</Link>.
          </ShopBrandNote>

          <FormGroup>
            <Label>Item Name *</Label>
            <Input
              type="text"
              value={formData.itemName}
              onChange={(e) => handleInputChange('itemName', e.target.value)}
              placeholder="e.g. Floral Summer Dress, Handcrafted Ring"
              maxLength={150}
            />
            {errors.itemName && <ErrorText>{errors.itemName}</ErrorText>}
          </FormGroup>

          <ListingCategorySelect
            value={formData}
            onChange={handleTaxonomyChange}
            errors={errors}
            categories={LUXURY_ITEM_CATEGORIES}
            ui={{ FormGroup, Label, Select, ErrorText }}
          />

          <ListingSizeColorFields
            attrs={listingAttrs}
            category={formData.category}
            color={formData.color}
            selectedSizes={selectedSizes}
            onColorChange={(v) => handleInputChange('color', v)}
            onToggleSize={(sizeValue) => {
              setSelectedSizes((prev) =>
                prev.includes(sizeValue) ? prev.filter((s) => s !== sizeValue) : [...prev, sizeValue]
              );
              if (errors.sizes) setErrors((prev) => ({ ...prev, sizes: '' }));
            }}
            errors={errors}
            namePrefix="boutique"
          />

          <FormGroup>
            <Label>Description *</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your item in detail: materials, dimensions, care instructions, etc. (minimum 25 characters)"
              rows={5}
            />
            {errors.description && <ErrorText>{errors.description}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>Price (EGP) *</Label>
            <PriceNote>
              Buyers pay the listed price. After the item is accepted, Flea deducts a 5% commission from your payout. Delivery is paid separately by the buyer.
            </PriceNote>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="Enter price in EGP"
              min="0"
              step="1"
            />
            {errors.price && <ErrorText>{errors.price}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <ListingMediaUploadField
              mediaItems={selectedImages}
              onChange={setSelectedImages}
              maxMedia={12}
              minMedia={3}
              minPhotos={1}
              minRecommendedPhotos={3}
              idPrefix="boutique-item"
              label="Photos & videos"
              pickTimeUpload
              uploadApiRef={mediaUploadApiRef}
            />
          </FormGroup>

          <ButtonGroup>
            <PrimaryButton type="submit" disabled={uploading || savingAddress}>
              {uploading || savingAddress ? 'Listing...' : 'List Boutique Item'}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => navigate('/boutiques')}>
              Cancel
            </SecondaryButton>
          </ButtonGroup>
        </Form>
      </FormCard>
    </PageWrapper>
  );
};

export default OfferBoutiqueItemPage;
